import { useEffect } from "react";
import { RequestType, geocode, setDefaults } from "react-geocode";

import { TFunction, getGoogleKey } from "utils";

import { COUNTRY_CODE, ZIP_CODE_COUNT_BY_COUNTRY } from "consts";

enum ADDRESS_COMPONENT_TYPES {
  ADMINISTRATIVE_AREA_LEVEL_1 = "administrative_area_level_1",
  POSTAL_CODE = "postal_code",
  LOCALITY = "locality",
  COUNTRY = "country",
}

type AddressComponent = {
  long_name?: string;
  short_name?: string;
  types?: ADDRESS_COMPONENT_TYPES[];
};

export const useAddressValidation = ({ country, t }: { country: string; t: TFunction }) => {
  useEffect(() => {
    // @ts-expect-error convert this to ts
    setDefaults({
      key: getGoogleKey(),
      language: country ?? COUNTRY_CODE.GERMANY,
      region: country ?? COUNTRY_CODE.GERMANY,
    });
  }, [country]);

  const validateZipCode = async (value?: string) => {
    if (!value) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.zipMissing")));
    if (!country) return Promise.reject(new Error(t("cropExplorer:onboardingModal.missingCountry"))); // Handle missing country
    const region = await searchZip(value, country);

    if (!region) {
      return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.zip"))); // Handle wrong zip code
    }
  };

  const searchZipOverGoogleMapsAPI = async ({ country, zipCode }: { country: string; zipCode: string }) => {
    // @ts-expect-error convert this to ts
    const response = await geocode(RequestType.ADDRESS, zipCode, { components: `country:${country}` });
    if (response.status !== "OK") return null;

    const foundRegion = response.results[0].address_components.find(
      // @ts-expect-error convert this to ts
      (component) => component.types.indexOf(ADDRESS_COMPONENT_TYPES.ADMINISTRATIVE_AREA_LEVEL_1) >= 0
    )?.long_name;
    if (foundRegion) return foundRegion;

    const foundCountry = response.results[0].address_components.find(
      // @ts-expect-error convert this to ts
      (component) => component.types.includes("country") && component.types.includes("political")
    )?.short_name;
    const isPartialMatch = response.results[0].partial_match;

    return !isPartialMatch && foundCountry.toLowerCase() === country.toLowerCase() ? foundCountry : null;
  };

  const searchZip = async (zipCode: string, country: string) => {
    // @ts-expect-error convert this to ts
    const zipCodeCountByCountry = ZIP_CODE_COUNT_BY_COUNTRY[country];
    if (zipCode?.length !== zipCodeCountByCountry) return null;

    const regionFromGoogleMapsAPI = await searchZipOverGoogleMapsAPI({ country, zipCode });

    return regionFromGoogleMapsAPI;
  };

  const getFullAddress = ({
    addressStreet,
    addressNumber,
    addressCity,
    zip,
    region,
  }: {
    addressStreet?: string;
    addressNumber?: string;
    addressCity?: string;
    zip?: string;
    region?: string;
  }) => {
    if ([addressStreet, addressNumber, addressCity, zip].some((param) => !param)) return;

    const address = `${addressStreet} ${addressNumber}, ${zip} ${addressCity} ${region} ${country}`;
    return address;
  };

  const verifyAddressComponent = (addressComponents: AddressComponent[], value: string, type: ADDRESS_COMPONENT_TYPES): boolean => {
    if (!addressComponents?.length) return false;

    const isVerifiedComponent = addressComponents.some(
      (addressComponent) => addressComponent.long_name?.toLowerCase() === value.toLowerCase() && addressComponent.types?.includes(type)
    );
    return isVerifiedComponent;
  };

  const validateRegion = async ({
    addressStreet,
    addressNumber,
    addressCity,
    zip,
    region,
  }: {
    addressStreet?: string;
    addressNumber?: string;
    addressCity?: string;
    zip?: string;
    region?: string;
  }) => {
    if (!region) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.regionMissing")));
    const address = getFullAddress({
      addressStreet,
      addressNumber,
      addressCity,
      zip,
      region,
    });
    if (!address) return;

    const response = await geocode(RequestType.ADDRESS, address);
    if (!response || response.results?.length !== 1) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.region")));

    const isVerifiedRegion = verifyAddressComponent(response.results[0].address_components, region, ADDRESS_COMPONENT_TYPES.ADMINISTRATIVE_AREA_LEVEL_1);
    if (!isVerifiedRegion) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.region")));

    return Promise.resolve();
  };

  const validateZipCodeWithFullAddress = async ({
    addressStreet,
    addressNumber,
    addressCity,
    zip,
    region,
  }: {
    addressStreet?: string;
    addressNumber?: string;
    addressCity?: string;
    zip?: string;
    region?: string;
  }) => {
    if (!zip) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.zipMissing")));
    const address = getFullAddress({
      addressStreet,
      addressNumber,
      addressCity,
      zip,
      region,
    });
    if (!address) return;

    const response = await geocode(RequestType.ADDRESS, address);
    if (!response) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.zipAddress")));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isVerifiedZipCode = response.results.some((address: any) =>
      verifyAddressComponent(address.address_components! as AddressComponent[], zip, ADDRESS_COMPONENT_TYPES.POSTAL_CODE)
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isVerifiedCountry = response.results.every((address: any) =>
      address.address_components.some(
        (component: AddressComponent) =>
          component.types?.includes(ADDRESS_COMPONENT_TYPES.COUNTRY) && component.short_name?.toLowerCase() === country.toLowerCase()
      )
    );
    if (!isVerifiedZipCode || !isVerifiedCountry) return Promise.reject(new Error(t("onboarding:farmDetails.errorMessage.zipAddress")));

    return Promise.resolve();
  };

  const isCityValid = async (city: string, countryCode: string): Promise<boolean> => {
    if (!city || !countryCode) return false;

    try {
      // @ts-expect-error convert this to ts
      const response = await geocode(RequestType.ADDRESS, city, { components: `country:${countryCode}` });

      if (response.status !== "OK" || !response.results?.length) {
        return false;
      }

      const addressComponents = response.results[0].address_components;

      const foundCountry = addressComponents.find((component: AddressComponent) => component.types?.includes(ADDRESS_COMPONENT_TYPES.COUNTRY))?.short_name;

      const foundCity = addressComponents.find((component: AddressComponent) => component.types?.includes(ADDRESS_COMPONENT_TYPES.LOCALITY))?.long_name;

      const isCityMatch = foundCity?.toLowerCase() === city.toLowerCase();
      const isCountryMatch = foundCountry?.toLowerCase() === countryCode.toLowerCase();

      return isCityMatch && isCountryMatch;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Geocoding failed for city:", city, error);
      return false;
    }
  };

  return { validateRegion, validateZipCode, validateZipCodeWithFullAddress, isCityValid };
};
